<script setup lang="ts">
  import { ref, onMounted, computed, watch } from 'vue'
  import type { AdProps } from '../../ad_ops/providers/types'
  import type { DplxApiAd } from '../../ad_ops/providers/dplxapi'
  import { apiAdProviders } from '../../ad_ops/providers/providers'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import { useViewabilityTracker } from '../../composables/ad-ops/useViewabilityTracker'

  const props = defineProps<AdProps>()
  const { track } = useAnalytics()

  const ad = ref<DplxApiAd | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const adContainerRef = useTemplateRef<HTMLElement | null>('adContainerRef')
  const hasTrackedImpression = ref(false)

  const provider = computed(() => apiAdProviders[props.adType])
  const providerName = computed(() => provider.value?.name || 'unknown')

  const { renderWhenCurrentElementIsVisible } = useAdFunctions()

  // Set up viewability tracker with 50% threshold and 1 second view time
  const viewabilityTracker = useViewabilityTracker(
    () => {
      trackViewable()
    },
    {
      threshold: 0.5, // 50% visible
      viewTime: 1000, // 1 second
      trackMultiple: false, // Only track once per ad
    }
  )

  const placementAttributes = computed(() => ({
    'data-ad-provider': providerName.value,
    'data-ad-instance-id': props.adInstanceId || '',
    'data-tile-position': props.tilePosition || -1,
    'data-render-registered': false,
    'data-index': provider.value.adIndex || 0,
  }))

  const buildEventData = () =>
    provider.value?.getEventData?.({
      placement: props.placement,
      brand: ad.value?.brand,
      clickUrl: ad.value?.clickUrl,
      creativeId: ad.value?.creativeId,
      tilePosition: provider.value.adIndex || 0,
    })

  async function fetchAd() {
    if (!provider.value) {
      error.value = `Ad provider "${props.adType}" not found`
      console.error(
        `[GenericAPIAd] Provider "${props.adType}" not found in apiAdProviders registry`
      )
      return
    }

    isLoading.value = true
    error.value = null
    ad.value = null

    try {
      const fetchedAd = await provider.value.fetchAd()

      if (fetchedAd) {
        ad.value = fetchedAd
        trackImpression()
        // Start viewability tracking once ad is loaded and visible
        if (adContainerRef.value) {
          viewabilityTracker.startTracking(adContainerRef.value)
        }
      } else {
        error.value = 'No ad available'
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch ad'
      console.error(`[${provider.value.name}] Error fetching ad:`, err)
    } finally {
      isLoading.value = false
    }
  }

  function trackImpression() {
    if (!ad.value || !provider.value || hasTrackedImpression.value) return

    provider.value.trackImpression(ad.value)
    hasTrackedImpression.value = true

    const eventData = buildEventData()
    if (eventData) track('NtpAd_Rendered', eventData)
  }

  // Once-only firing is owned by useViewabilityTracker (trackMultiple: false);
  // re-checking hasTrackedViewable here would always be true by this point.
  function trackViewable() {
    if (!ad.value || !provider.value) return

    if (provider.value.trackViewable) {
      provider.value.trackViewable(ad.value)
    }

    if (provider.value.trackPixel && ad.value.pixelTracker) {
      provider.value.trackPixel(ad.value)
    }

    const eventData = buildEventData()
    if (eventData) track('NtpAd_Viewable', eventData)
  }

  function handleClick() {
    if (!ad.value || !provider.value) return

    provider.value.handleAdClick?.(ad.value)

    const eventData = buildEventData()
    if (eventData) track('NtpAd_Clicked', eventData)
  }

  // Reset viewability tracking when ad changes
  watch(ad, (newAd, oldAd) => {
    if (newAd?.id !== oldAd?.id) {
      viewabilityTracker.reset()
    }
  })

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      adContainerRef.value,
      props.lazyRender,
      props.sticky,
      fetchAd
    )
  })
</script>

<template>
  <div
    ref="adContainerRef"
    class="relative transition duration-300"
    :class="props.style?.wrapper"
    v-bind="placementAttributes"
  >
    <div>
      <a
        v-if="ad"
        :key="ad?.id"
        :href="ad?.clickUrl"
        target="_blank"
        class="relative transition duration-300"
        :class="props.style?.wrapper"
        role="article"
        :aria-label="'Partner Content'"
        v-bind="placementAttributes"
        rel="noopener noreferrer sponsored"
        @click="handleClick"
      >
        <NuxtImg
          v-if="props.style?.image"
          class="transition duration-300"
          role="img"
          :aria-label="`Image for Partner Content`"
          :class="props.style.image"
          :src="ad?.imageUrl"
          :alt="ad?.title"
        />
        <template
          v-if="
            props.style?.headline ||
            (props.style?.publisher && props.style.publisher.wrapper)
          "
        >
          <Typography
            v-if="props.style.headline"
            class="transition duration-500"
            :class="props.style.headline"
            data-testid="ad-headline"
          >
            {{ ad?.title }}
          </Typography>
          <div
            v-if="props.style?.publisher && props.style.publisher.wrapper"
            :class="props.style.publisher.wrapper"
            class="flex items-center gap-1 order-2 px-4 mb-[2px]"
          >
            <div class="flex flex-row gap-1">
              <Typography
                :class="props.style.publisher.text"
                data-testid="ad-brand"
              >
                {{ ad?.brand }} | AD
              </Typography>
            </div>
          </div>
        </template>
      </a>
    </div>
  </div>
</template>
